import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Pill, Stepper, type PillVariant } from '../../../ds/components';
import type { Tone } from '../types';
import styles from './wizard.module.css';

export const WIZARD_STEPS = [
  { id: 'basics', label: 'Grunddaten' },
  { id: 'uploads', label: 'Unterlagen' },
  { id: 'classification', label: 'Klassifizierung' },
  { id: 'calculation', label: 'Kalkulation' },
  { id: 'checklist', label: 'Checkliste & Freigabe' },
] as const;

export function pillVariantOf(tone: Tone): PillVariant {
  return tone === 'accent' ? 'accent' : tone === 'neutral' ? 'neutral' : tone;
}

export interface WizardShellProps {
  crumbs: { label: string; href?: string | undefined }[];
  title: string;
  pill: { label: string; tone: Tone };
  /** Right end of the header: save state + „Abbrechen" / „Zurückziehen" / „Zur Objektakte". */
  headerEnd: ReactNode;
  /** Zero-based current step; `WIZARD_STEPS.length` marks every step as done (Objekt anlegen 09). */
  step: number;
  onStepClick?: ((index: number) => void) | undefined;
  main: ReactNode;
  sidebar: ReactNode;
  /** Footer bar (14/32); omitted on the released screen. */
  footer?: ReactNode | undefined;
}

/** The frame of Objekt anlegen 01–10: header, five-step progress, 1fr · 360 grid and the footer bar. */
export function WizardShell({
  crumbs,
  title,
  pill,
  headerEnd,
  step,
  onStepClick,
  main,
  sidebar,
  footer,
}: WizardShellProps) {
  return (
    <div className={styles.page}>
      <div className={styles.stage}>
        <div className={styles.head}>
          <div className={styles.headText}>
            <nav className={styles.crumbs} aria-label="Pfad">
              {crumbs.map((crumb, index) => (
                <Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 ? <span aria-hidden="true">/</span> : null}
                  {crumb.href ? (
                    <Link to={crumb.href} className={styles.crumbLink}>
                      {crumb.label}
                    </Link>
                  ) : (
                    <span aria-current={index === crumbs.length - 1 ? 'page' : undefined}>{crumb.label}</span>
                  )}
                </Fragment>
              ))}
            </nav>
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{title}</h1>
              <Pill variant={pillVariantOf(pill.tone)}>{pill.label}</Pill>
            </div>
          </div>
          <div className={styles.headEnd}>{headerEnd}</div>
        </div>
        <Stepper
          steps={WIZARD_STEPS}
          current={step}
          orientation="horizontal"
          onStepClick={onStepClick}
          label="Schritte"
        />
        <div className={styles.grid}>
          <div className={styles.column}>{main}</div>
          <div className={styles.column}>{sidebar}</div>
        </div>
      </div>
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}

/** Title + text head of the main cards („Worum geht es?", „Ankaufskalkulation", …). */
export function CardIntro({
  title,
  text,
  end,
}: {
  title: string;
  text?: string | undefined;
  end?: ReactNode | undefined;
}) {
  return (
    <div className={styles.cardHead}>
      <div className={styles.cardHeadText}>
        <h2 className={styles.cardTitle}>{title}</h2>
        {text ? <p className={styles.cardText}>{text}</p> : null}
      </div>
      {end}
    </div>
  );
}
